import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import { authentication_routes } from "@/router/authentication_routes";
import store from "@/store";
import {
  GET_USER_DETAILS,
  NAVIGATE_USER_BASED_ON_ROLE,
  REHYDRATE_STATE,
  SET_USER,
  USER_STATE
} from "@/store/modules/auth/constants";
import { candidates_routes } from "@/router/candidate_routes";
import { recruiter_routes } from "@/router/recruiter_routes";
import { settings_routes } from "@/router/settings_routes";
import { UserState } from "@/store/modules/auth/interfaces";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "",
    redirect: {
      path: "auth"
    }
  },
  {
    path: "/book-demo",
    name: "book-demo",
    component: () => import("@/views/BookDemo.vue"),
    meta: {
      user_state: [
        UserState.UNAUTHENTICATED,
        UserState.CANDIDATE,
        UserState.RECRUITER,
        UserState.RECRUITER_ADMIN
      ]
    }
  },
  authentication_routes,
  // onboarding_routes,
  candidates_routes,
  recruiter_routes,
  settings_routes,
  {
    path: "/job/:id",
    name: "view-job-details",
    component: () => import("@/views/shared/ViewJobDetails.vue"),
    meta: {
      user_state: []
    }
  },
  {
    path: "/approval-pending",
    name: "approval-pending",
    component: () => import("@/views/shared/ApprovalPending.vue"),
    meta: {
      user_state: []
    }
  },
  {
    path: "/error",
    component: () => import("@/views/ErrorPage.vue"),
    meta: {
      user_state: [UserState.ROOT_ERROR]
    }
  },
  {
    path: "*",
    component: () => import("@/views/NotFound.vue"),
    meta: {
      user_state: [
        UserState.UNAUTHENTICATED,
        UserState.EMAIL_VERIFICATION_PENDING,
        UserState.EMAIL_VERIFICATION_COMPLETED,
        UserState.ONBOARDING_PENDING,
        UserState.RECRUITER,
        UserState.RECRUITER_ADMIN,
        UserState.CANDIDATE,
        UserState.PENDING_VERIFICATION
      ]
    }
  }
];

const router = new VueRouter({
  routes,
  mode: "history"
});

const default_title = "GT-Assessment";

router.beforeEach(async (to, from, next) => {
  // SSO ticket entry always wins: a candidate opening their assignment link must
  // authenticate as the ticket's candidate, never as whoever was logged in on
  // this browser before. Drop any existing session so the guard doesn't rehydrate
  // and redirect the previous user before AuthLogin processes the new ticket.
  const isTicketEntry =
    (to.name === "login" || to.path.includes("/auth/login")) &&
    !!to.query.ticket;
  if (isTicketEntry) {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    store.commit(`auth/${SET_USER}`, null);
    store.commit(`auth/${USER_STATE}`, UserState.UNAUTHENTICATED);
    return next();
  }

  // If user press link in email to reset password
  if (
    to.meta &&
    to.meta.user_state.includes(UserState.RESET_PASSWORD) &&
    to.meta.link === true
  ) {
    store.commit(`auth/${USER_STATE}`, UserState.RESET_PASSWORD);
  }
  let user_state = store.getters[`auth/${USER_STATE}`];
  const user_exist = store.getters[`auth/${GET_USER_DETAILS}`];
  // If user not exist but user token exist in local storage
  // Rehydrating state & init user data (retry on transient failure to avoid logout on reload)
  if (
    !user_exist &&
    localStorage.getItem("token") &&
    user_state !== UserState.ROOT_ERROR &&
    user_state !== UserState.RESET_PASSWORD &&
    user_state !== UserState.RESET_PASSWORD_COMPLETED
  ) {
    const maxRetries = 3;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const ok = await store.dispatch(`auth/${REHYDRATE_STATE}`);
      if (ok) break;
      if (attempt < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      }
    }
    user_state = store.getters[`auth/${USER_STATE}`];
  }
  // Fetching user state from meta tag
  const meta_user_state = to.meta
    ? to.meta.user_state
    : [UserState.UNAUTHENTICATED];

  // Special case: Allow authenticated users to access verify-email if not email confirmed
  const is_verify_email_route =
    to.name === "verify-email" || to.path.includes("/verify-email");
  if (is_verify_email_route && user_exist) {
    const user = store.getters[`auth/${GET_USER_DETAILS}`] as {
      is_email_confirmed?: boolean;
    } | null;
    if (user && !user.is_email_confirmed) {
      next();
      return;
    }
  }

  // If user state not include in next route meta tag
  // Cancel route
  if (!meta_user_state.includes(user_state)) {
    const not_allowed = [UserState.UNAUTHENTICATED, UserState.ROOT_ERROR];
    if (!user_exist && !not_allowed.includes(user_state)) {
      await store.dispatch(`auth/${NAVIGATE_USER_BASED_ON_ROLE}`);
      next();
    } else {
      // If user not logged in & user viewing internal page ==> Add redirect url
      if (
        user_state === UserState.UNAUTHENTICATED &&
        !to.query.redirect &&
        !to.fullPath.toLowerCase().includes("error")
      ) {
        next({
          name: "login",
          query: { redirect: to.fullPath }
        });
      } else {
        // If profile fetching is in progress => always navigate to profile loading page
        if (user_state === UserState.CANDIDATE_PROFILE_PENDING)
          next("/candidate/profile-loading");
        else next(false);
      }
    }
  } else {
    next();
  } // Navigate user
});

// Updating title
router.beforeResolve((to, from, next) => {
  let title = "";
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (to.name) title = to.name.replace(/-/g, " ").toUpperCase();
  if (title.includes("HCMS")) {
    title = title.replace("HCMS", "GT-ASSESSMENT");
  }
  // Per-tenant title if branding is set, else the default.
  const brand_title =
    store.getters["branding/GET_BRANDING"]?.title || default_title;
  Vue.nextTick(() => {
    document.title = `${title} | ${brand_title}` || brand_title;
    const meta = document.querySelector('meta[property="og:title"]');
    if (meta)
      meta.setAttribute("content", `${title} | ${brand_title}` || brand_title);
  });
  next();
});

export default router;
